import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { OrdersService } from '../../../core/services/orders/orders.service';
import { AccountsService } from '../../../core/services/accounts/accounts.service';
import { BrandService } from '../../../core/services/brand/brand.service';
import { ModelService } from '../../../core/services/model/model.service';
import { CampaignsService } from '../../../core/services/campaigns/campaigns.service';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { DocumentsTypeService } from '../../../core/services/documentType/documents-type.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { AuthStateService } from '../../../core/services/auth-state/auth-state-service.service';
import { FormatNumberPipe, FormatNumberPipe2, FormatNumberPipe3 } from '../../../core/pipe/custom-pipes';

interface WizardStep {
  key: string;
  label: string;
  valid: boolean;
}

interface WizardDocument {
  file: File;
  type: string;
}

@Component({
  selector: 'app-sql-wizard',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, TranslateModule,
    NgSelectModule, FormatNumberPipe, FormatNumberPipe2, FormatNumberPipe3,
  ],
  templateUrl: './sql-wizard.component.html',
  styleUrl: './sql-wizard.component.css',
})
export class SqlWizardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Wizard state
  currentStep = 0;
  steps: WizardStep[] = [
    { key: 'contract', label: 'inputs.contract', valid: false },
    { key: 'documents', label: 'inputs.documents', valid: false },
    { key: 'client', label: 'inputs.client', valid: false },
    { key: 'delivery', label: 'inputs.delivery', valid: false },
    { key: 'notes', label: 'inputs.notes', valid: false },
    { key: 'confirm', label: 'buttons.confirm', valid: false },
  ];

  // Accordion state for step 1
  activeSection: 'agreement' | 'vehicle' = 'agreement';

  loading = true;
  submitting = false;
  wizardToken: string | null = null;
  prefillData: any = null;
  brokerReference: string | null = null;
  supplierReference: string | null = null;
  vendorReference: string | null = null;
  validationFailures: string[] = [];

  // Step 1: Partnership
  partnershipForm: FormGroup;
  brokerData: any[] = [];
  supplierData: any[] = [];
  vendorData: any[] = [];

  // Step 2: Vehicle
  vehicleForm: FormGroup;
  vehicleMode: 'manual' | 'campaign_select' | 'campaign_confirmed' = 'manual';
  useCampaign = false; // legacy compat
  brands: any[] = [];
  models: any[] = [];
  campaignsData: any[] = [];
  selectedCampaignRef = '';
  matrixData: any = null;
  matrixColumns: string[] = [];
  matrixRows: string[] = [];
  matrixDisplay: any = {};
  showIva = true;
  selectedMatrixCol = '';
  selectedMatrixRow = '';
  vehicleStatusOptions = ['new', 'km0', 'used'];

  // Step 2: Documents — stored locally, uploaded in final submit
  activeDocTab: 'client' | 'order' = 'client';
  orderDocuments: WizardDocument[] = [];
  clientDocuments: WizardDocument[] = [];
  allDocumentTypes: any[] = [];
  get orderDocumentTypes(): any[] { return this.allDocumentTypes.filter(t => t.type === 'order'); }
  get clientDocumentTypes(): any[] { return this.allDocumentTypes.filter(t => t.type === this.clientForm?.value?.type); }
  extractedClientData: any = null;

  // Step 4: Client
  clientForm: FormGroup;
  clientTypeOptions = ['individual', 'organization', 'self_employed'];

  // Step: Delivery
  deliveryForm: FormGroup;
  deliveryPlaceOptions: any[] = [];
  deliveryUrgencyOptions: any[] = ['high', 'medium', 'low', 'custom'];

  // Step: Notes
  notesForm: FormGroup;

  // Guarantee (avalista)
  guaranteeId: string | null = null;
  guaranteeData: any = null;
  showGuaranteeForm = false;
  searchingGuarantee = false;
  guaranteeTaxIdSearch = '';
  guaranteeNotFound = false;
  guaranteeForm: FormGroup;

  // Assignee (persona de contacto para organizations)
  assigneeId: string | null = null;
  assigneeData: any = null;
  showAssigneeForm = false;
  searchingAssignee = false;
  assigneeTaxIdSearch = '';
  assigneeNotFound = false;
  assigneeForm: FormGroup;

  get taxRate(): number {
    return this.authStateService.getSupplierTax() || 0.21;
  }

  // Has campaign from prefill (came already resolved)
  get hasPrefillCampaign(): boolean {
    return !!(this.prefillData?.payload?.campaign_id);
  }

  // Legacy compat getters
  get campaignLocked(): boolean { return this.vehicleMode === 'campaign_confirmed'; }
  get showConfigSelector(): boolean { return this.vehicleMode === 'campaign_select'; }

  toggleConfigSelector(): void {
    this.vehicleMode = this.vehicleMode === 'campaign_select' ? 'campaign_confirmed' : 'campaign_select';
  }

  switchToManual(): void {
    this.vehicleMode = 'manual';
    this.useCampaign = false;
    // No limpiar campaña/matrix — se preservan para poder volver
  }

  switchToCampaignSelect(): void {
    this.vehicleMode = 'campaign_select';
    this.useCampaign = true;
    // Si hay campaña previa, recargar matrix directamente (sin pasar por onCampaignSelect)
    if (this.selectedCampaignRef && !this.matrixData) {
      this.reloadMatrix(this.selectedCampaignRef);
    }
  }

  private reloadMatrix(campaignRef: string): void {
    this.alertService.loading('swal_messages.please_wait');
    this.campaignsService.getMatrix(campaignRef)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.alertService.close();
          this.matrixData = res.data;
          this.updateMatrixDisplay();
        },
        error: () => {
          this.alertService.close();
        },
      });
  }

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private ordersService: OrdersService,
    private accountsService: AccountsService,
    private brandService: BrandService,
    private modelService: ModelService,
    private campaignsService: CampaignsService,
    private clientsService: ClientsService,
    private documentsTypeService: DocumentsTypeService,
    private alertService: AlertService,
    private authStateService: AuthStateService,
  ) {
    this.partnershipForm = this.fb.group({
      broker_id: [this.authStateService.getAccountReference() || '', Validators.required],
      supplier_id: ['', Validators.required],
      vendor_id: ['', Validators.required],
    });

    this.vehicleForm = this.fb.group({
      vehicle: [''],
      vehicle_brand_id: [''],
      vehicle_model_id: [''],
      vehicle_model_year: [''],
      vehicle_status: [''],
      vehicle_trim: [''],
      colour: [''],
      campaign_id: [''],
      campaign_reference: [''],
      duration: [''],
      yearly_kms: [''],
      total_kms: [''],
      monthly_fee_base: [null],
      monthly_fee_tax: [null],
      monthly_fee_total: [null],
      type: [''],
      include_maintenance: [false],
      include_insurance: [false],
      include_tires: [false],
      include_replacement_vehicle: [false],
      initial_payment: [null],
      vehicle_discount_code: [''],
    });

    this.clientForm = this.fb.group({
      tax_id: ['', Validators.required],
      type: ['individual', Validators.required],
      name: ['', Validators.required],
      lastname: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      nationality: [''],
      birthplace_country: [''],
      birth_date: [null],
      document_expiration_date: [null],
      billing_address: ['', Validators.required],
      billing_postal_code: ['', [Validators.required, Validators.pattern(/^\d{4,5}$/)]],
      billing_city: ['', Validators.required],
      billing_state: ['', Validators.required],
    });

    const personFormFields = {
      tax_id: ['', Validators.required],
      type: ['individual', Validators.required],
      name: ['', Validators.required],
      lastname: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      nationality: [''],
      birthplace_country: [''],
      birth_date: [null],
      document_expiration_date: [null],
      billing_address: ['', Validators.required],
      billing_postal_code: ['', [Validators.required, Validators.pattern(/^\d{4,5}$/)]],
      billing_city: ['', Validators.required],
      billing_state: ['', Validators.required],
    };
    this.guaranteeForm = this.fb.group({ ...personFormFields });
    this.assigneeForm = this.fb.group({ ...personFormFields });

    this.deliveryForm = this.fb.group({
      delivery_urgency: [''],
      delivery_place: [''],
      delivery_address: [''],
      delivery_state: [''],
      delivery_postal_code: [''],
      delivery_city: [''],
    });

    this.notesForm = this.fb.group({
      notes: [''],
    });
  }

  toggleSection(section: 'agreement' | 'vehicle'): void {
    this.activeSection = section;
  }

  ngOnInit(): void {
    this.wizardToken = this.route.snapshot.queryParams['token'] || null;
    this.loadInitialData();
  }

  // --- Data loading ---

  loadInitialData(): void {
    this.loading = true;

    // Load accounts (brokers, suppliers, vendors)
    this.accountsService.getData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const accounts = res.data || [];
          this.brokerData = accounts.filter((a: any) => a.type === 'broker');
          this.supplierData = accounts.filter((a: any) => a.type === 'supplier');
          this.vendorData = accounts.filter((a: any) => a.type === 'vendor');

          // After accounts load, load prefill if we have a token
          if (this.wizardToken) {
            this.loadPrefill();
          } else {
            this.loading = false;
          }
        },
        error: () => {
          this.loading = false;
        }
      });

    // Load brands in parallel
    this.brandService.getData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => { this.brands = res.data || []; },
      });

    // Load campaigns in parallel
    this.campaignsService.getCampaigns()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => { this.campaignsData = res.data || []; },
      });

    // Load delivery place options
    this.ordersService.getDeliveryData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => { this.deliveryPlaceOptions = res.data?.places || []; },
      });

    // Load document type catalog
    this.documentsTypeService.getSelectType()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => { this.allDocumentTypes = res.data || []; },
      });
  }

  loadPrefill(): void {
    this.ordersService.getSqlPrefill(this.wizardToken!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.prefillData = res.data;
          this.brokerReference = res.data?.broker_reference || null;
          this.supplierReference = res.data?.supplier_reference || null;
          this.vendorReference = res.data?.vendor_reference || null;

          // If order already exists, redirect using order_url from backend
          const orderUrl = res.data?.order_url || res.data?.order?.order_url;
          if (orderUrl) {
            window.location.href = orderUrl;
            return;
          }

          this.applyPrefill(res.data?.payload);
          this.loading = false;
        },
        error: (err: any) => {
          this.loading = false;
          const errors = err?.error;
          if (errors && typeof errors === 'object') {
            this.alertService.error(errors).then(() => {
              this.router.navigate(['/orders']);
            });
            return;
          }
          this.alertService.error('swal_messages.error_occurred').then(() => {
            this.router.navigate(['/orders']);
          });
        }
      });
  }

  applyPrefill(payload: any): void {
    if (!payload) return;

    // Partnership
    if (payload.broker_id) this.partnershipForm.patchValue({ broker_id: payload.broker_id });
    if (payload.supplier_id) this.partnershipForm.patchValue({ supplier_id: payload.supplier_id });
    if (payload.vendor_id) this.partnershipForm.patchValue({ vendor_id: payload.vendor_id });

    // Vehicle (manual mode) — prefill may already have campaign data resolved
    const vehiclePatch: any = {};
    if (payload.vehicle) vehiclePatch.vehicle = payload.vehicle;
    if (payload.vehicle_model_year) vehiclePatch.vehicle_model_year = String(payload.vehicle_model_year);
    if (payload.vehicle_status) vehiclePatch.vehicle_status = payload.vehicle_status;
    if (payload.vehicle_trim) vehiclePatch.vehicle_trim = payload.vehicle_trim;
    if (payload.colour) vehiclePatch.colour = payload.colour;
    if (payload.duration) vehiclePatch.duration = String(payload.duration);
    if (payload.yearly_kms) vehiclePatch.yearly_kms = String(payload.yearly_kms);
    if (payload.type) vehiclePatch.type = payload.type;
    if (payload.campaign_id) vehiclePatch.campaign_id = payload.campaign_id;
    if (payload.campaign_reference) vehiclePatch.campaign_reference = payload.campaign_reference;
    if (payload.include_maintenance != null) vehiclePatch.include_maintenance = !!payload.include_maintenance;
    if (payload.include_insurance != null) vehiclePatch.include_insurance = !!payload.include_insurance;
    if (payload.include_tires != null) vehiclePatch.include_tires = !!payload.include_tires;
    if (payload.include_replacement_vehicle != null) vehiclePatch.include_replacement_vehicle = !!payload.include_replacement_vehicle;
    if (payload.initial_payment != null) vehiclePatch.initial_payment = payload.initial_payment;
    if (payload.vehicle_discount_code) vehiclePatch.vehicle_discount_code = payload.vehicle_discount_code;

    // Calcular campos derivados que falten
    const duration = +(vehiclePatch.duration || payload.duration || 0);
    const yearlyKms = +(vehiclePatch.yearly_kms || payload.yearly_kms || 0);
    const totalKms = +(vehiclePatch.total_kms || payload.total_kms || 0);
    const feeBase = +(vehiclePatch.monthly_fee_base ?? payload.monthly_fee_base ?? 0);
    const feeTax = +(vehiclePatch.monthly_fee_tax ?? payload.monthly_fee_tax ?? 0);
    const feeTotal = +(vehiclePatch.monthly_fee_total ?? payload.monthly_fee_total ?? 0);

    // total_kms = yearly_kms * (duration / 12)
    if (!totalKms && yearlyKms && duration) {
      vehiclePatch.total_kms = String(Math.round(yearlyKms * (duration / 12)));
    }
    // yearly_kms = total_kms / (duration / 12)
    if (!yearlyKms && totalKms && duration) {
      vehiclePatch.yearly_kms = String(Math.round(totalKms / (duration / 12)));
    }
    // monthly_fee_tax = total - base
    if (!feeTax && feeBase && feeTotal) {
      vehiclePatch.monthly_fee_tax = +(feeTotal - feeBase).toFixed(2);
    }
    // monthly_fee_total = base + tax
    if (!feeTotal && feeBase && feeTax) {
      vehiclePatch.monthly_fee_total = +(feeBase + feeTax).toFixed(2);
    }
    // monthly_fee_base = total - tax
    if (!feeBase && feeTotal && feeTax) {
      vehiclePatch.monthly_fee_base = +(feeTotal - feeTax).toFixed(2);
    }
    // monthly_fee_total = base * (1 + taxRate) cuando solo tenemos base
    if (!feeTotal && !feeTax && feeBase) {
      vehiclePatch.monthly_fee_tax = +(feeBase * this.taxRate).toFixed(2);
      vehiclePatch.monthly_fee_total = +(feeBase * (1 + this.taxRate)).toFixed(2);
    }
    // monthly_fee_base = total / (1 + taxRate) cuando solo tenemos total
    if (!feeBase && !feeTax && feeTotal) {
      vehiclePatch.monthly_fee_base = +(feeTotal / (1 + this.taxRate)).toFixed(2);
      vehiclePatch.monthly_fee_tax = +(feeTotal - (feeTotal / (1 + this.taxRate))).toFixed(2);
    }

    if (Object.keys(vehiclePatch).length) {
      this.vehicleForm.patchValue(vehiclePatch);
    }

    // Si viene con campaña del CRM → sincronizar selectedCampaignRef + campaign_reference + modo confirmado
    if (payload.campaign_id) {
      this.selectedCampaignRef = payload.campaign_id;
      this.vehicleMode = 'campaign_confirmed';
      this.useCampaign = true;

      // Buscar nombre de la campaña en campaignsData (puede no estar cargado aún)
      const trySetCampaignLabel = () => {
        const found = this.campaignsData.find((c: any) => c.reference === payload.campaign_id);
        if (found) {
          const label = found.supplier_name ? (found.supplier_name + ' - ' + found.name) : found.name;
          this.vehicleForm.patchValue({ campaign_reference: label });
        }
      };

      if (this.campaignsData.length > 0) {
        trySetCampaignLabel();
      } else {
        // campaignsData se carga async — reintentar cuando esté listo
        const interval = setInterval(() => {
          if (this.campaignsData.length > 0) {
            clearInterval(interval);
            trySetCampaignLabel();
          }
        }, 200);
        setTimeout(() => clearInterval(interval), 5000);
      }
    }

    // Brand → load models → set model
    if (payload.vehicle_brand_id) {
      this.vehicleForm.patchValue({ vehicle_brand_id: payload.vehicle_brand_id });
      this.modelService.getDataByBrand(payload.vehicle_brand_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.models = res.data || [];
            if (payload.vehicle_model_id) {
              this.vehicleForm.patchValue({ vehicle_model_id: payload.vehicle_model_id });
            }
          },
        });
    }

    // Client
    if (payload.client) {
      this.clientForm.patchValue({
        tax_id: payload.client.tax_id || '',
        type: payload.client.type || 'individual',
        name: payload.client.name || '',
        lastname: payload.client.lastname || '',
        email: payload.client.email || '',
        phone: payload.client.phone || '',
        nationality: payload.client.nationality || '',
        birthplace_country: payload.client.birthplace_country || '',
        birth_date: payload.client.birth_date || null,
        document_expiration_date: payload.client.document_expiration_date || null,
        billing_address: payload.client.billing_address || '',
        billing_postal_code: payload.client.billing_postal_code || '',
        billing_city: payload.client.billing_city || '',
        billing_state: payload.client.billing_state || '',
      });

      // Assignee from prefill (resolve if UUID present)
      if (payload.client.assignee_id) {
        this.assigneeId = payload.client.assignee_id;
        this.clientsService.getClientByReference(payload.client.assignee_id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => { this.assigneeData = res.data; },
          });
      }
    }

    // Guarantee from prefill (resolve if UUID present)
    if (payload.guarantee_id) {
      this.guaranteeId = payload.guarantee_id;
      this.clientsService.getClientByReference(payload.guarantee_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => { this.guaranteeData = res.data; },
        });
    }
  }

  // --- Navigation ---

  goToStep(index: number): void {
    if (index < this.currentStep) {
      this.currentStep = index;
    }
  }

  nextStep(): void {
    this.validateCurrentStep(() => {
      if (this.currentStep < this.steps.length - 1) {
        this.steps[this.currentStep].valid = true;
        this.currentStep++;
      }
    });
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  // --- Validation ---

  validateCurrentStep(onSuccess: () => void): void {
    const step = this.steps[this.currentStep];
    let scope = step.key;

    // Steps that don't validate via API
    if (scope === 'documents' || scope === 'delivery' || scope === 'notes' || scope === 'confirm') {
      onSuccess();
      return;
    }

    // Step "contract" validates partnership + vehicle scopes sequentially
    if (scope === 'contract') {
      this.validateScope('partnership', () => {
        this.validateScope('vehicle', onSuccess);
      });
      return;
    }

    this.validateScope(scope, onSuccess);
  }

  private validateScope(scope: string, onSuccess: () => void): void {
    const body: any = {
      scope,
      ...this.partnershipForm.getRawValue(),
    };

    if (scope === 'vehicle' || scope === 'client') {
      Object.assign(body, this.getVehiclePayload());
    }
    if (scope === 'client') {
      body.client = this.clientForm.getRawValue();
    }

    this.alertService.loading('swal_messages.please_wait');
    this.ordersService.validateOrder(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.alertService.close();
          if (res.data?.valid) {
            this.validationFailures = [];
            onSuccess();
          } else {
            this.validationFailures = res.data?.failures || [];
          }
        },
        error: (err: any) => {
          this.alertService.close();
          const errors = err?.error;
          if (errors && typeof errors === 'object') {
            this.validationFailures = [];
            this.alertService.error(errors);
          } else {
            this.alertService.error('swal_messages.general.error');
          }
        }
      });
  }

  // --- Step 2: Vehicle / Campaign ---

  onBrandChange(brandRef: string): void {
    this.vehicleForm.patchValue({ vehicle_model_id: '' });
    this.models = [];
    if (brandRef) {
      this.modelService.getDataByBrand(brandRef)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => { this.models = res.data || []; },
        });
    }
  }

  toggleCampaignMode(): void {
    this.useCampaign = !this.useCampaign;
    if (!this.useCampaign) {
      this.vehicleForm.patchValue({ campaign_id: '' });
      this.selectedCampaignRef = '';
      this.matrixData = null;
    }
  }

  onCampaignSelect(campaign: any): void {
    if (!campaign) {
      this.selectedCampaignRef = '';
      this.vehicleForm.patchValue({ campaign_id: '', campaign_reference: '' });
      this.matrixData = null;
      return;
    }
    // ng-select con bindValue puede emitir el string reference en vez del objeto
    if (typeof campaign === 'string') {
      campaign = this.campaignsData.find((c: any) => c.reference === campaign);
      if (!campaign) return;
    }
    this.selectedCampaignRef = campaign.reference;
    const campaignLabel = campaign.supplier_name ? (campaign.supplier_name + ' - ' + campaign.name) : campaign.name;
    this.vehicleForm.patchValue({ campaign_id: campaign.reference, campaign_reference: campaignLabel });

    // Load matrix with loading modal
    this.alertService.loading('swal_messages.please_wait');
    this.campaignsService.getMatrix(campaign.reference)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.alertService.close();
          this.matrixData = res.data;
          this.updateMatrixDisplay();
        },
        error: () => {
          this.alertService.close();
          this.alertService.error('swal_messages.general.error');
        },
      });
  }

  updateMatrixDisplay(): void {
    if (!this.matrixData) return;
    const matrix = this.showIva ? this.matrixData.matrix_total : this.matrixData.matrix_base;
    if (!matrix) { this.matrixColumns = []; this.matrixRows = []; this.matrixDisplay = {}; return; }
    this.matrixDisplay = matrix;
    this.matrixColumns = Object.keys(matrix).sort((a, b) => +a - +b);
    const kmSet = new Set<string>();
    Object.values(matrix).forEach(row => Object.keys(row as object).forEach(km => kmSet.add(km)));
    this.matrixRows = Array.from(kmSet).sort((a, b) => +a - +b);
  }

  onIvaToggle(event: Event): void {
    this.showIva = (event.target as HTMLInputElement).checked;
    this.updateMatrixDisplay();
  }

  selectMatrixCell(col: string, row: string): void {
    if (this.matrixDisplay[col]?.[row] == null) return;
    this.selectedMatrixCol = col;
    this.selectedMatrixRow = row;

    const base = this.matrixData?.matrix_base?.[col]?.[row] ?? 0;
    const total = this.matrixData?.matrix_total?.[col]?.[row] ?? 0;
    const tax = +(total - base).toFixed(2);
    const duration = +col;
    const yearlyKms = +row;

    this.vehicleForm.patchValue({
      duration: String(duration),
      yearly_kms: String(yearlyKms),
      total_kms: String(Math.round(yearlyKms * (duration / 12))),
      monthly_fee_base: +(base).toFixed(2),
      monthly_fee_tax: +(tax).toFixed(2),
      monthly_fee_total: +(total).toFixed(2),
    });

    // Confirmar campaña al seleccionar celda → mostrar labels
    this.vehicleMode = 'campaign_confirmed';
  }

  // --- Step 3: Documents (stored locally, uploaded in final submit as multipart) ---

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement)?.classList.add('over');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement)?.classList.remove('over');
  }

  onDropZone(event: DragEvent, docType: 'order' | 'client'): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement)?.classList.remove('over');
    const files = event.dataTransfer?.files;
    if (!files?.length) return;
    const target = docType === 'order' ? this.orderDocuments : this.clientDocuments;
    Array.from(files).forEach(file => {
      target.push({ file, type: '' });
    });
  }

  getFileThumbnail(file: File): string {
    const name = file.name.toLowerCase();
    if (/\.(jpg|jpeg|png|gif|webp)$/.test(name)) return 'assets/icons/jpg.svg';
    if (/\.pdf$/.test(name)) return 'assets/icons/pdf.svg';
    if (/\.(doc|docx)$/.test(name)) return 'assets/icons/doc.svg';
    if (/\.(xls|xlsx|csv)$/.test(name)) return 'assets/icons/xls.svg';
    if (/\.(zip|rar)$/.test(name)) return 'assets/icons/zip.svg';
    return 'assets/icons/file.svg';
  }

  onDocumentAdd(event: Event, docType: 'order' | 'client'): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    const target = docType === 'order' ? this.orderDocuments : this.clientDocuments;
    const startIndex = target.length;

    files.forEach(file => {
      target.push({ file, type: '' });
    });

    // Sugerir tipo de documento al backend
    const entity = docType === 'order' ? 'orders' : 'clients';
    const names = files.map(f => f.name);
    this.documentsTypeService.suggestType(entity, names)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const suggestions: any[] = res.data || [];
          suggestions.forEach((suggestion, i) => {
            if (suggestion?.type && suggestion.type !== 'unknown' && target[startIndex + i]) {
              target[startIndex + i].type = suggestion.type;
            }
          });
        },
        error: () => { /* silencioso — el tipo queda vacío */ }
      });

    // For client docs, also extract DNI data
    if (docType === 'client') {
      const extractFormData = new FormData();
      files.forEach(file => extractFormData.append('file[]', file));

      this.alertService.loading('swal_messages.please_wait');
      this.clientsService.uploadDocument(extractFormData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.alertService.close();
            if (res.data) {
              this.extractedClientData = res.data;
              this.applyExtractedClientData(res.data);
            }
          },
          error: () => { this.alertService.close(); }
        });
    }

    input.value = '';
  }

  removeDocument(index: number, docType: 'order' | 'client'): void {
    if (docType === 'order') this.orderDocuments.splice(index, 1);
    else this.clientDocuments.splice(index, 1);
  }

  onDocumentTypeChange(index: number, docType: 'order' | 'client', typeSlug: string): void {
    const target = docType === 'order' ? this.orderDocuments : this.clientDocuments;
    if (target[index]) {
      target[index].type = typeSlug;
    }
  }

  applyExtractedClientData(data: any): void {
    if (!data) return;
    const current = this.clientForm.getRawValue();
    // DNI data wins over CRM for accrediable fields, but don't overwrite manual edits
    this.clientForm.patchValue({
      tax_id: data.tax_id || current.tax_id,
      name: data.name || current.name,
      lastname: data.lastname || current.lastname,
      billing_address: data.billing_address || current.billing_address,
      billing_city: data.billing_city || current.billing_city,
      billing_state: data.billing_state || current.billing_state,
      // email and phone: CRM wins (not in DNI)
    });
  }

  // --- Guarantee (avalista) ---

  searchGuaranteeByTaxId(): void {
    const taxId = this.guaranteeTaxIdSearch.trim();
    if (!taxId) return;
    // Cannot be same as main client
    if (taxId === this.clientForm.value.tax_id) {
      this.alertService.warning('swal_messages.general.client_guarantee_same');
      return;
    }
    this.searchingGuarantee = true;
    this.guaranteeNotFound = false;
    this.showGuaranteeForm = false;
    this.clientsService.checkTaxId(taxId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.searchingGuarantee = false;
          if (res.data?.reference) {
            this.guaranteeId = res.data.reference;
            this.guaranteeData = res.data;
            this.guaranteeNotFound = false;
          } else {
            this.guaranteeData = null;
            this.guaranteeNotFound = true;
            this.guaranteeForm.patchValue({ tax_id: taxId });
          }
        },
        error: () => {
          this.searchingGuarantee = false;
          this.guaranteeData = null;
          this.guaranteeNotFound = true;
          this.guaranteeForm.patchValue({ tax_id: taxId });
        }
      });
  }

  /**
   * Construye el payload para POST /clients/new alineado con form-add-client.
   * Agrega status:true y formatea phone como (prefix)number si no tiene paréntesis.
   */
  private buildClientPayload(form: FormGroup): any {
    const raw = form.getRawValue();
    const payload: any = {
      status: true,
      type: raw.type || 'individual',
      tax_id: raw.tax_id || '',
      name: raw.name || '',
      lastname: raw.lastname || '',
      email: raw.email || '',
      billing_address: raw.billing_address || '',
      billing_postal_code: raw.billing_postal_code || '',
      billing_city: raw.billing_city || '',
      billing_state: raw.billing_state || '',
    };
    // Phone: si no tiene formato (prefix)number, agregar (+34) por defecto
    const phone = (raw.phone || '').trim();
    if (phone && !phone.startsWith('(')) {
      payload.phone = `(+34)${phone}`;
    } else {
      payload.phone = phone;
    }
    // Campos opcionales — solo enviar si tienen valor
    if (raw.nationality) payload.nationality = raw.nationality;
    if (raw.birthplace_country) payload.birthplace_country = raw.birthplace_country;
    if (raw.birth_date) payload.birth_date = raw.birth_date;
    if (raw.document_expiration_date) payload.document_expiration_date = raw.document_expiration_date;
    return payload;
  }

  cancelGuaranteeForm(): void {
    this.showGuaranteeForm = false;
    this.guaranteeForm.reset();
  }

  onPersonDocUpload(event: Event, target: 'guarantee' | 'assignee'): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.processPersonDoc(input.files, target);
    input.value = '';
  }

  onPersonDocDrop(event: DragEvent, target: 'guarantee' | 'assignee'): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement)?.classList.remove('over');
    const files = event.dataTransfer?.files;
    if (!files?.length) return;
    this.processPersonDoc(files, target);
  }

  private processPersonDoc(files: FileList, target: 'guarantee' | 'assignee'): void {
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('file[]', file));

    this.alertService.loading('swal_messages.please_wait');
    this.clientsService.uploadDocument(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.alertService.close();
          const data = res.data;
          if (!data) return;

          if (data.reference) {
            // Cliente ya existe → asignar directamente
            if (target === 'guarantee') {
              if (data.reference === this.clientForm.value.tax_id) {
                this.alertService.warning('swal_messages.general.client_guarantee_same');
                return;
              }
              this.guaranteeId = data.reference;
              this.guaranteeData = data;
              this.showGuaranteeForm = false;
              this.guaranteeNotFound = false;
            } else {
              this.assigneeId = data.reference;
              this.assigneeData = data;
              this.showAssigneeForm = false;
              this.assigneeNotFound = false;
            }
            this.alertService.success('swal_messages.complete_success');
          } else {
            // Pre-rellenar formulario
            const form = target === 'guarantee' ? this.guaranteeForm : this.assigneeForm;
            const patch: any = {};
            if (data.tax_id) patch.tax_id = data.tax_id;
            if (data.name) patch.name = data.name;
            if (data.lastname) patch.lastname = data.lastname;
            if (data.email) patch.email = data.email;
            if (data.phone) patch.phone = data.phone;
            if (data.nationality) patch.nationality = data.nationality;
            if (data.birthplace_country) patch.birthplace_country = data.birthplace_country;
            if (data.birth_date) patch.birth_date = data.birth_date;
            if (data.document_expiration_date) patch.document_expiration_date = data.document_expiration_date;
            if (data.billing_address) patch.billing_address = data.billing_address;
            if (data.billing_postal_code) patch.billing_postal_code = data.billing_postal_code;
            if (data.billing_city) patch.billing_city = data.billing_city;
            if (data.billing_state) patch.billing_state = data.billing_state;
            form.patchValue(patch);
            this.alertService.success('swal_messages.complete_success');
          }
        },
        error: (err: any) => {
          this.alertService.close();
          this.alertService.error(err?.error || 'swal_messages.error_occurred');
        },
      });
  }

  createGuarantee(): void {
    if (this.guaranteeForm.invalid) return;
    this.alertService.loading('swal_messages.please_wait');
    const payload = this.buildClientPayload(this.guaranteeForm);
    this.clientsService.addData(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.alertService.close();
          this.guaranteeId = res.data?.reference || null;
          this.guaranteeData = res.data;
          this.showGuaranteeForm = false;
          this.guaranteeNotFound = false;
          this.alertService.success('swal_messages.complete_success');
        },
        error: (err: any) => {
          this.alertService.close();
          this.alertService.error(err?.error || 'swal_messages.general.error');
        }
      });
  }

  removeGuarantee(): void {
    this.guaranteeId = null;
    this.guaranteeData = null;
    this.showGuaranteeForm = false;
    this.guaranteeTaxIdSearch = '';
  }

  // --- Assignee (persona de contacto) ---

  searchAssigneeByTaxId(): void {
    const taxId = this.assigneeTaxIdSearch.trim();
    if (!taxId) return;
    this.searchingAssignee = true;
    this.assigneeNotFound = false;
    this.showAssigneeForm = false;
    this.clientsService.checkTaxId(taxId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.searchingAssignee = false;
          if (res.data?.reference) {
            this.assigneeId = res.data.reference;
            this.assigneeData = res.data;
            this.assigneeNotFound = false;
          } else {
            this.assigneeData = null;
            this.assigneeNotFound = true;
            this.assigneeForm.patchValue({ tax_id: taxId });
          }
        },
        error: () => {
          this.searchingAssignee = false;
          this.assigneeData = null;
          this.assigneeNotFound = true;
          this.assigneeForm.patchValue({ tax_id: taxId });
        }
      });
  }

  cancelAssigneeForm(): void {
    this.showAssigneeForm = false;
    this.assigneeForm.reset();
  }

  createAssignee(): void {
    if (this.assigneeForm.invalid) return;
    this.alertService.loading('swal_messages.please_wait');
    const payload = this.buildClientPayload(this.assigneeForm);
    this.clientsService.addData(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.alertService.close();
          this.assigneeId = res.data?.reference || null;
          this.assigneeData = res.data;
          this.showAssigneeForm = false;
          this.assigneeNotFound = false;
          this.alertService.success('swal_messages.complete_success');
        },
        error: (err: any) => {
          this.alertService.close();
          this.alertService.error(err?.error || 'swal_messages.general.error');
        }
      });
  }

  removeAssignee(): void {
    this.assigneeId = null;
    this.assigneeData = null;
    this.showAssigneeForm = false;
    this.assigneeTaxIdSearch = '';
  }

  // --- Step 5: Submit (multipart/form-data) ---

  getVehiclePayload(): any {
    const v = this.vehicleForm.getRawValue();
    const payload: any = {};
    if (v.vehicle) payload.vehicle = v.vehicle;
    if (v.vehicle_brand_id) payload.vehicle_brand_id = v.vehicle_brand_id;
    if (v.vehicle_model_id) payload.vehicle_model_id = v.vehicle_model_id;
    if (v.vehicle_model_year) payload.vehicle_model_year = +v.vehicle_model_year;
    if (v.vehicle_status) payload.vehicle_status = v.vehicle_status;
    if (v.vehicle_trim) payload.vehicle_trim = v.vehicle_trim;
    if (v.colour) payload.colour = v.colour;
    if (v.campaign_id) payload.campaign_id = v.campaign_id;
    if (v.campaign_reference) payload.campaign_reference = v.campaign_reference;
    if (v.duration) payload.duration = +v.duration;
    if (v.yearly_kms) payload.yearly_kms = +v.yearly_kms;
    if (v.total_kms) payload.total_kms = +v.total_kms;
    if (v.monthly_fee_base != null) payload.monthly_fee_base = parseFloat((+v.monthly_fee_base).toFixed(2));
    if (v.monthly_fee_tax != null) payload.monthly_fee_tax = parseFloat((+v.monthly_fee_tax).toFixed(2));
    if (v.monthly_fee_total != null) payload.monthly_fee_total = parseFloat((+v.monthly_fee_total).toFixed(2));
    if (v.type) payload.type = v.type;
    if (v.include_maintenance) payload.include_maintenance = v.include_maintenance;
    if (v.include_insurance) payload.include_insurance = v.include_insurance;
    if (v.include_tires) payload.include_tires = v.include_tires;
    if (v.include_replacement_vehicle) payload.include_replacement_vehicle = v.include_replacement_vehicle;
    if (v.initial_payment != null && v.initial_payment !== '') payload.initial_payment = +v.initial_payment;
    if (v.vehicle_discount_code) payload.vehicle_discount_code = v.vehicle_discount_code;
    return payload;
  }

  buildSubmitFormData(): FormData {
    const formData = new FormData();

    // order[campo] fields
    const partnership = this.partnershipForm.getRawValue();
    const vehicle = this.getVehiclePayload();
    const delivery = this.deliveryForm.getRawValue();
    const notes = this.notesForm.getRawValue();
    const orderFields: any = { ...partnership, ...vehicle, ...delivery, ...notes };
    if (this.guaranteeId) {
      orderFields.guarantee_id = this.guaranteeId;
    }
    Object.entries(orderFields).forEach(([key, value]) => {
      if (value != null && value !== '') {
        formData.append(`order[${key}]`, String(value));
      }
    });

    // client[campo] fields
    const client: any = { ...this.clientForm.getRawValue() };
    if (this.assigneeId) {
      client.assignee_id = this.assigneeId;
    }
    Object.entries(client).forEach(([key, value]) => {
      if (value != null && value !== '') {
        formData.append(`client[${key}]`, String(value));
      }
    });

    // wizard_token
    if (this.wizardToken) {
      formData.append('wizard_token', this.wizardToken);
    }

    // order_documents[] + order_document_types[] (aligned by index)
    this.orderDocuments.forEach(doc => {
      formData.append('order_documents[]', doc.file);
      formData.append('order_document_types[]', doc.type || '');
    });

    // client_documents[] + client_document_types[] (aligned by index)
    this.clientDocuments.forEach(doc => {
      formData.append('client_documents[]', doc.file);
      formData.append('client_document_types[]', doc.type || '');
    });

    return formData;
  }

  onSubmit(): void {
    if (this.submitting) return;

    this.submitting = true;
    this.alertService.loading('swal_messages.please_wait');

    this.ordersService.createSqlOrder(this.buildSubmitFormData())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.submitting = false;
          this.alertService.successOk('swal_messages.orders.order_created')
            .then((result) => {
              if (result.isConfirmed && res.data?.reference) {
                this.router.navigate(['/orders/sqls', res.data.reference]);
              }
            });
        },
        error: (error: any) => {
          this.submitting = false;
          if (error.code === 429) {
            // Idempotency — already created
            this.alertService.successOk('swal_messages.orders.order_created')
              .then(() => this.router.navigate(['/orders/sqls']));
            return;
          }
          const errors = error?.error;
          if (errors && typeof errors === 'object') {
            this.alertService.error(errors);
          } else {
            this.alertService.error('swal_messages.general.error');
          }
        }
      });
  }

  get hasDeliveryData(): boolean {
    const v = this.deliveryForm.value;
    return !!(v.delivery_urgency || v.delivery_place || v.delivery_address || v.delivery_state || v.delivery_postal_code || v.delivery_city);
  }

  getDeliveryPlaceName(key: string): string {
    return this.deliveryPlaceOptions.find((p: any) => p.key === key)?.name || key;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
